/**
 * Factory function for creating analyze functions
 * Reduces boilerplate across all apps
 */

import { apiPost, cancellableRequest } from './apiClient';

export interface AnalyzeOptions {
  retry?: number;
  timeout?: number;
}

/**
 * Creates an analyze function for an app
 *
 * @param endpoint - API endpoint (e.g., '/iSteelElement/')
 * @param mapInputToPayload - Function to transform form data to API payload
 * @param options - Request options (retry, timeout)
 *
 * @example
 * export const analyze = createAnalyzeFunction(
 *   '/iSteelElement/',
 *   mapInputToPayload,
 *   { retry: 2, timeout: 15000 }
 * );
 */
export function createAnalyzeFunction<TInput, TPayload, TResponse>(endpoint: string, mapInputToPayload: (input: TInput) => TPayload, options: AnalyzeOptions = {}) {
  const { retry = 2, timeout = 15000 } = options;

  return async (data: TInput): Promise<TResponse> => {
    const payload = mapInputToPayload(data);
    // console.log(`Sending payload to ${endpoint}:`, payload);

    const response = await apiPost<TResponse>(endpoint, payload, {
      retry,
      timeout
    });

    // console.log(`Received response from ${endpoint}:`, response);
    return response;
  };
}

/**
 * Creates a cancellable analyze function for an app
 *
 * @param endpoint - API endpoint
 * @param mapInputToPayload - Function to transform form data to API payload
 * @param options - Request options
 *
 * @example
 * export const analyzeCancellable = createCancellableAnalyze(
 *   '/iSteelElement/',
 *   mapInputToPayload,
 *   { retry: 2, timeout: 15000 }
 * );
 */
export function createCancellableAnalyze<TInput, TPayload, TResponse>(endpoint: string, mapInputToPayload: (input: TInput) => TPayload, options: AnalyzeOptions = {}) {
  const { retry = 2, timeout = 15000 } = options;

  return (data: TInput) => {
    const payload = mapInputToPayload(data);
    return cancellableRequest<TResponse>(endpoint, {
      method: 'POST',
      body: payload,
      retry,
      timeout
    });
  };
}

export function createAppApi<TInput, TPayload, TResponse>(endpoint: string, mapInputToPayload: (input: TInput) => TPayload, options: AnalyzeOptions = {}) {
  // Most calculator pages need the same pair of exports:
  // a normal analyze function and a cancellable variant wired to the same endpoint/options.
  return {
    analyze: createAnalyzeFunction<TInput, TPayload, TResponse>(endpoint, mapInputToPayload, options),
    analyzeCancellable: createCancellableAnalyze<TInput, TPayload, TResponse>(endpoint, mapInputToPayload, options)
  };
}
